/**
 * Project registry endpoints.
 */

import type {
  ActiveProject,
  OnboardingCandidate,
  Project,
  ProjectDashboardResponse,
  ProjectDiscoveryResponse,
  ProjectOnboardingStatusResponse,
  ProjectOnboardRequest,
  RejectedOnboardingResponse,
  UnregisteredProject,
  UnverifiedProjectListResponse,
} from '@/types/project'
import type { ProjectMembersResponse } from '@/types/auth'
import { apiClient } from './client'

// Collection endpoints include the trailing slash to skip FastAPI's 307
// redirect; item-level endpoints (`/projects/{id}`) do not.
export const projects = {
  list: async (): Promise<Project[]> => {
    const rows: Project[] = []
    const limit = 100
    for (let skip = 0; ; skip += limit) {
      const response = await apiClient.get<Project[]>('/api/v1/projects/', {
        params: { skip, limit },
      })
      rows.push(...response.data)
      if (response.data.length < limit) return rows
    }
  },

  get: async (id: string): Promise<Project> => {
    const response = await apiClient.get<Project>(`/api/v1/projects/${id}`)
    return response.data
  },

  create: async (data: Partial<Project>): Promise<Project> => {
    const response = await apiClient.post<Project>('/api/v1/projects/', data)
    return response.data
  },

  update: async (id: string, data: Partial<Project>): Promise<Project> => {
    const response = await apiClient.put<Project>(
      `/api/v1/projects/${id}`,
      data
    )
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/projects/${id}`)
  },

  getActive: async (params?: {
    days?: number
    skip?: number
    limit?: number
  }): Promise<ActiveProject[]> => {
    const response = await apiClient.get<ActiveProject[]>(
      '/api/v1/projects/active',
      { params }
    )
    return response.data
  },

  getDashboard: async (params?: {
    days?: number
    limit?: number
    recent_limit?: number
  }): Promise<ProjectDashboardResponse> => {
    const response = await apiClient.get<ProjectDashboardResponse>(
      '/api/v1/projects/active/dashboard',
      { params }
    )
    return response.data
  },

  getUnregistered: async (): Promise<UnregisteredProject[]> => {
    const response = await apiClient.get<UnregisteredProject[]>(
      '/api/v1/projects/unregistered'
    )
    return response.data
  },

  // ── PRD-0064: membership and unverified-project review ─────────────────

  /**
   * Fetch the active member list for a project.
   * One JOIN query server-side; never a per-member fan-out.
   */
  getProjectMembers: async (
    projectId: string
  ): Promise<ProjectMembersResponse> => {
    const response = await apiClient.get<ProjectMembersResponse>(
      `/api/v1/projects/${projectId}/members/`
    )
    return response.data
  },

  /**
   * Fetch auto-created projects that have not yet been reviewed.
   * Paginated (skip/limit), with optional sort (`[-]field`, whitelist name /
   * created_at) and q (case-insensitive name substring). Returns the paged
   * envelope (items + total_count), never a bare array.
   */
  getUnverifiedProjects: async (params?: {
    skip?: number
    limit?: number
    sort?: string
    q?: string
  }): Promise<UnverifiedProjectListResponse> => {
    const response = await apiClient.get<UnverifiedProjectListResponse>(
      '/api/v1/projects/unverified/',
      { params }
    )
    return response.data
  },

  /**
   * Mark a project as operator-verified.
   * Idempotent: a re-verify on an already-verified project is a no-op server-side.
   * Returns the updated Project row.
   */
  verifyProject: async (projectId: string): Promise<Project> => {
    const response = await apiClient.post<Project>(
      `/api/v1/projects/${projectId}/verify/`
    )
    return response.data
  },

  // ── PRD-0168: guided onboarding, status polling, rejection feedback ────

  /**
   * Create a verified project and grant its creator project-scoped write, in
   * one atomic backend transaction — the guided-onboarding entry point, so
   * the caller is never locked out of the project they just created.
   */
  onboard: async (payload: ProjectOnboardRequest): Promise<Project> => {
    const response = await apiClient.post<Project>(
      '/api/v1/projects/onboard',
      payload
    )
    return response.data
  },

  /** Poll whether a project is awaiting credentials, active, or rejecting. */
  getOnboardingStatus: async (
    projectId: string
  ): Promise<ProjectOnboardingStatusResponse> => {
    const response = await apiClient.get<ProjectOnboardingStatusResponse>(
      `/api/v1/projects/${projectId}/onboarding-status`
    )
    return response.data
  },

  /**
   * Unregistered sources whose telemetry was rejected at the binding gate —
   * feeds the home-page card that names each source and deep-links onboarding.
   */
  getRejectedOnboarding: async (): Promise<RejectedOnboardingResponse[]> => {
    const response = await apiClient.get<RejectedOnboardingResponse[]>(
      '/api/v1/projects/rejected-onboarding'
    )
    return response.data
  },

  /**
   * The caller's own machine-announced unregistered folders, captured when the
   * collector was installed — the primary slug source for the onboarding
   * wizard, available immediately without first running an agent session.
   * Self-scoped server-side, so the UI consumes the rows as-is.
   */
  getOnboardingCandidates: async (): Promise<OnboardingCandidate[]> => {
    const response = await apiClient.get<OnboardingCandidate[]>(
      '/api/v1/projects/onboarding-candidates'
    )
    return response.data
  },

  /**
   * Every project a name or slug may mean, as a bounded set with the ways each
   * one matched — the onboarding wizard's duplicate check before create. A set,
   * not a pick: the caller shows it and never chooses for the person.
   */
  lookup: async (name: string): Promise<ProjectDiscoveryResponse> => {
    const response = await apiClient.get<ProjectDiscoveryResponse>(
      '/api/v1/projects/lookup',
      { params: { name } }
    )
    return response.data
  },
}
